import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DocumentItemPartial } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'ra-ui';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditService } from '../../../services/edit-service';
import { MenuOptionsService } from '../../../services/menu-options.service';
import { TextEditingService } from '../../../services/text-editing.service';

/**
 * "Inserir campo" no fim da folha (opção A do protótipo, 2026-09-25).
 *
 * Antes era uma pílula clara no fim da barra do topo e passava despercebida. Chegou a ficar
 * preso ao pé da área visível, mas cobria os cards no meio da rolagem: agora mora só depois
 * do último campo, e entra com animação cada vez que aparece na tela. Clicado, ele VIRA
 * o menu de campos (placement `morph`, ver MenuOptionsComponent). O campo novo entra
 * depois do que está em edição (a linha azul entre os campos mostra onde); sem nenhum em
 * edição, no fim do nível aberto (seção ou pasta). Sai da frente quando a barra da seleção
 * em lote aparece, que ocupa o mesmo lugar.
 */
@Component({
    selector: 'insert-field-fab',
    templateUrl: 'insert-field-fab.component.html',
    styleUrl: 'insert-field-fab.component.scss',
    imports: [RaIconComponent, TranslatePipe],
})
export class InsertFieldFabComponent {
    /** Itens do nível aberto (seção ou pasta), na ordem da folha. */
    public items = input<DocumentItemPartial[]>([]);

    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    private _menu = inject(MenuOptionsService);
    private _textEditing = inject(TextEditingService);

    private _button = viewChild<ElementRef<HTMLElement>>('button');

    /** O botão está na tela: liga a animação de entrada (e religa quando volta). */
    public revealed = signal(false);

    /** Campo em edição no instante do toque: o clique tira o foco dele antes de chegar aqui. */
    private _editingAtPress: string | null = null;

    public visible = computed(
        () =>
            !!this.editDataService.currentSession() &&
            !this.editDataService.currentSessionIsDisabled() &&
            !this.editDataService.documentIsBlocked() &&
            this.editDataService.selectedItems().length === 0
    );

    /** O menu cresceu a partir deste botão e está por cima dele: o botão some embaixo. */
    public morphed = computed(() => this._menu.morphOpen() && this.open());

    /** O menu está aberto a partir deste botão. */
    public open = computed(
        () =>
            !this._menu.isMenuOptionsHidden() &&
            !!this._button() &&
            this._menu.anchor() === this._button()?.nativeElement
    );

    constructor() {
        // O menu abre aqui quando ninguém diz onde (documento vazio ao carregar).
        effect(() => {
            const button = this._button()?.nativeElement ?? null;
            untracked(() => {
                this._menu.defaultAnchor = button;
                this._menu.defaultPlacement = button ? 'morph' : 'below';
            });
        });

        // Sem IntersectionObserver (testes, navegador antigo) o botão só aparece.
        const observer =
            typeof IntersectionObserver === 'undefined'
                ? null
                : new IntersectionObserver(
                      ([entry]) => this.revealed.set(entry.isIntersecting),
                      // Qualquer pedaço à vista já revela: o topo do invólucro é o botão (a
                      // folga fica embaixo). Com margem, o botão ficava invisível e clicável na
                      // faixa de baixo da tela.
                      { threshold: 0 }
                  );
        if (observer) observer.observe(inject<ElementRef<HTMLElement>>(ElementRef).nativeElement);
        else this.revealed.set(true);

        inject(DestroyRef).onDestroy(() => {
            observer?.disconnect();
            this._menu.defaultAnchor = null;
            this._menu.defaultPlacement = 'below';
        });
    }

    public rememberEditing(): void {
        this._editingAtPress = this._textEditing.editingFieldId();
    }

    public toggle(event: Event): void {
        const editing = this._editingAtPress;
        this._editingAtPress = null;

        if (this.open()) {
            this._menu.hideMenuOptions();
            return;
        }

        const items = this.items();
        const below = items.some(item => item._id === editing)
            ? editing
            : (items[items.length - 1]?._id ?? undefined);

        this._menu.showMenuOptions(undefined, below ?? undefined, event.currentTarget as HTMLElement, 'morph');
    }
}
